import moment from "moment";

const LIST_STATE_PREFIX = "flexio:factura-list-state:";

const reviveDateRange = (value) =>
  Array.isArray(value) && value.length === 2 ? value.map((date) => moment(date)) : null;

const serializeDateRange = (value) =>
  Array.isArray(value) && value.length === 2
    ? value.map((date) => moment(date).format("YYYY-MM-DD"))
    : null;

/**
 * Restores the list state (filters, page, sorting) a user left behind when they opened
 * an invoice, so coming back with "Back" doesn't force them to rebuild the search.
 * Falls back to `defaults` whenever nothing was stored or storage is unavailable.
 */
export const readFacturaListState = (storageKey, defaults) => {
  try {
    const raw = sessionStorage.getItem(`${LIST_STATE_PREFIX}${storageKey}`);
    if (!raw) return defaults;

    const saved = JSON.parse(raw);
    const savedFilters = saved?.filters || {};

    return {
      ...defaults,
      ...saved,
      filters: {
        ...defaults.filters,
        ...savedFilters,
        dateRange:
          "dateRange" in savedFilters
            ? reviveDateRange(savedFilters.dateRange)
            : defaults.filters?.dateRange
      }
    };
  } catch (error) {
    console.warn("Could not restore factura list state:", error);
    return defaults;
  }
};

/**
 * Stores the list state so it survives navigating into an invoice and back.
 * Session-scoped on purpose: a new tab or a new session starts from the defaults.
 */
export const writeFacturaListState = (storageKey, state) => {
  try {
    sessionStorage.setItem(
      `${LIST_STATE_PREFIX}${storageKey}`,
      JSON.stringify({
        ...state,
        filters: {
          ...state.filters,
          dateRange: serializeDateRange(state.filters?.dateRange)
        }
      })
    );
  } catch (error) {
    console.warn("Could not persist factura list state:", error);
  }
};

/**
 * Human-readable file name for a downloaded invoice, e.g. "factura-AH170826.pdf".
 */
export const buildFacturaPdfFilename = (facturaId) =>
  `factura-${String(facturaId || "invoice").replace(/[^\w.-]+/g, "-")}.pdf`;

/**
 * Saves a PDF response straight to the user's downloads instead of opening a tab,
 * so a single invoice can be downloaded from the invoice itself.
 */
export const downloadPdfResponse = (response, filename) => {
  const blob = new Blob([response], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

/**
 * Sort key for an invoice number. Handles both the current "AH170826(-A)" convention
 * and the legacy "YYYY-MM-DD-CLIENT-NN" ids, newest date first within each format.
 */
export const getFacturaIdSortValue = (facturaId) => {
  if (!facturaId) return "0-00000000-000";

  const legacy = /^(\d{4})-(\d{2})-(\d{2})-(.*)-(\d+)$/.exec(facturaId);
  if (legacy) {
    const [, year, month, day, identifier, increment] = legacy;
    return `1-${year}${month}${day}-${identifier}-${increment.padStart(3, "0")}`;
  }

  const current = /^([A-Za-z0-9]+?)(\d{2})(\d{2})(\d{2})(?:-([A-Za-z]+))?$/.exec(
    facturaId
  );
  if (current) {
    const [, initials, day, month, year, suffix = ""] = current;
    return `1-20${year}${month}${day}-${initials}-${suffix.padStart(3, "0")}`;
  }

  return `2-${facturaId}`;
};
